import {
  FIREBASE_VAPID_KEY,
  isFirebaseConfigured,
} from 'features/push-notification/constants';
import { pushNotificationApi } from 'features/push-notification/query';
import type { TPushSubscriptionPayload } from 'features/push-notification/types';
import { deleteToken, getToken } from 'firebase/messaging';
import { getFirebaseMessaging } from 'lib/firebase';
import type { TAppDispatch } from 'lib/stores';

/** vite-plugin-pwa (filename: sw.ts) → dist `/sw.ts` */
const PWA_SERVICE_WORKER_URL = '/sw.ts';

export const isPushSupported = (): boolean =>
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// App đang chạy standalone (đã Add to Home Screen) — iOS bắt buộc.
export const isStandaloneDisplayMode = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS Safari dùng cờ riêng navigator.standalone.
  (window.navigator as Navigator & { standalone?: boolean }).standalone ===
    true;

// Thiết bị iOS/iPadOS — nơi bắt buộc Add to Home Screen.
export const isIosDevice = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS 13+ giả macOS, nhận qua maxTouchPoints.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Đã cấu hình VAPID public key cho Firebase chưa.
export const isVapidConfigured = (): boolean => FIREBASE_VAPID_KEY.length > 0;

/** Lấy SW sẵn có (PWA) hoặc register `/sw.ts` — bắt buộc trước getToken FCM. */
export const getOrRegisterMessagingServiceWorker =
  async (): Promise<ServiceWorkerRegistration> => {
    let registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      registration = await navigator.serviceWorker.register(
        PWA_SERVICE_WORKER_URL,
        { scope: '/' }
      );
    }
    await navigator.serviceWorker.ready;
    return registration;
  };

/** Xin quyền + lấy FCM token + payload gửi BE (`platform: wp`). */
export const subscribeToPush =
  async (): Promise<TPushSubscriptionPayload | null> => {
    if (import.meta.env.DEV) {
      return null;
    }

    if (!isPushSupported() || !isVapidConfigured() || !isFirebaseConfigured()) {
      console.warn(
        '[FCM] Push skipped — thiếu VITE_FIREBASE_* / VAPID hoặc browser không hỗ trợ'
      );
      return null;
    }

    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      console.warn('[FCM] Notification permission=', permission);
      return null;
    }

    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    try {
      const registration = await getOrRegisterMessagingServiceWorker();
      const currentToken = await getToken(messaging, {
        vapidKey: FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (!currentToken) {
        console.warn('[FCM] Empty token — check VAPID / domain / SW');
        return null;
      }

      return { platform: 'wp', token: currentToken };
    } catch (err) {
      console.error('[FCM] getToken failed:', err);
      return null;
    }
  };

export const unsubscribeFromPush = async (): Promise<string | null> => {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return null;
  try {
    const currentToken = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
    });
    if (currentToken) {
      await deleteToken(messaging);
      return currentToken;
    }
  } catch (error) {
    console.warn('[FCM] unsubscribe failed:', error);
  }
  return null;
};

/** Dọn subscription khi logout — gọi TRƯỚC clearAuthToken và phải await. */
export const cleanupPushSubscriptionOnLogout = async (
  dispatch: TAppDispatch
): Promise<void> => {
  try {
    const token = await unsubscribeFromPush();
    if (!token) return;
    await dispatch(
      pushNotificationApi.endpoints.DeletePushSubscription.initiate({
        token,
      })
    );
  } catch {
    // logout không được fail vì dọn push lỗi
  }
};
